import { api, currentOrgId, setOfflineReads } from "../api/client";
import type { CustomerDetail, VendorDetail } from "../api/types";
import type { SalesDoc } from "../api/sales-types";
import { idb } from "./idb";

/*
 * Copies of what this person last opened, for when the server cannot be reached: the last 25 customers, 25 vendors and
 * 25 invoices (each as GET /admin/customers/<id>, /admin/purchases/vendors/<id> and /admin/sales/invoices/<id> answered),
 * and the lists the offline forms need ("lookup", wave 6): the purchases form's accounts, the active customers (names, state,
 * GSTIN and terms only: their emails, phones, PANs and addresses are left out) and items for a draft invoice or estimate,
 * the sales and projects settings, the projects and their tasks for a timesheet entry, and a record's notes. Kept in IndexedDB, never in the
 * service worker's cache; tagged with the staff member's id; each copy expires TTL_HOURS after it was saved; all of it
 * is deleted on sign-out, when the session expires, and when someone else signs in on this device.
 * "profile" (id, name, email, role name) lets a reload with no connection open the read-only offline screen.
 *
 * Organizations (server/orgs.js): every copy is kept under the organization it was read in ("o<id>:<path>", with `org`),
 * and is only ever answered to a screen working in that organization, so a switch never shows another organization's
 * customers or invoices, and two tabs in two organizations each see their own. Sign-in also deletes the copies of any
 * organization the person no longer belongs to. Sign-out deletes all of them.
 *
 * Wave 8 (docs/implemented-offline8.md): an onboarding as opened (its instalments, for a payment recorded offline), and the
 * onboarding and Billing invoice lists as the screens open them; a record may carry its own lifetime (`ttl`: the invoice
 * number block lasts what Settings › Number series says, 1 to 168 hours); keptLatest() reads the freshest kept list under a
 * path whatever its filters, for the offline screen after a reload (approvals, time waiting, Billing invoices).
 *
 * Wave 9 (docs/implemented-offline9.md): what the full forms read (the offline screen opens them, OfflineFullForms.tsx): the
 * currencies and price books, the GST settings (states), the inventory settings, and each module's custom field definitions
 * (never a record's values). keepLookups() reads them once after sign-in, so the forms work offline even if the person never
 * opened them online; the estimates, items and expenses kept as opened are listed on the offline screen too.
 */

export const TTL_HOURS = 24;
const KEEP = 25;
const TTL = TTL_HOURS * 3_600_000;

export type Kind = "customer" | "vendor" | "invoice" | "meta" | "lookup" | "profile" | "estimate" | "item" | "expense" | "block" | "onboarding";
/** `access` (wave 8): what the role may do in each module, so the offline screen offers only that (the server checks again). */
export type OfflineProfile = { id: string; full_name: string; email: string; role: string | null; org: number; org_name: string | null; access?: Record<string, string[]> };
export type Kept<T = unknown> = { key: string; kind: Kind; owner: string; org: number; label: string; sub: string; data: T; saved_at: number; ttl?: number };

const RULES: [RegExp, Kind][] = [
  [/^\/admin\/customers\/[A-Za-z0-9_-]+$/, "customer"],
  [/^\/admin\/sales\/invoices\/[A-Za-z0-9_-]+$/, "invoice"],
  [/^\/admin\/purchases\/meta$/, "meta"],
  [/^\/admin\/purchases\/vendors\/[A-Za-z0-9_-]+$/, "vendor"],
  [/^\/admin\/(customers|items)\?status=active$/, "lookup"],
  [/^\/admin\/(sales|projects)\/meta$/, "lookup"],
  [/^\/admin\/projects(\/[A-Za-z0-9_-]+|\?status=active)?$/, "lookup"],
  [/^\/admin\/notes\/(customers|vendors)\/[A-Za-z0-9_%-]+$/, "lookup"],
  // Wave 7: what an offline edit or approval starts from (docs/implemented-offline7.md). Lists only as the screens open
  // them, never a search (?q=), so typing in a search box does not fill the device.
  [/^\/admin\/sales\/estimates\/[A-Za-z0-9_-]+$/, "estimate"],
  [/^\/admin\/items\/[A-Za-z0-9_-]+$/, "item"],
  [/^\/admin\/purchases\/expenses\/[A-Za-z0-9_-]+$/, "expense"],
  [/^\/admin\/(items|sales\/(invoices|estimates)|purchases\/(expenses|vendors)|projects\/time-entries|approvals)(\?(?![^#]*\bq=)[^#]*)?$/, "lookup"],
  // Wave 8: an onboarding as opened (not its invoice or plan-terms sub-lists), and the onboarding and Billing invoice lists.
  [/^\/admin\/onboarding\/(?!invoices$|plan-terms$)[A-Za-z0-9_-]+$/, "onboarding"],
  [/^\/admin\/(onboarding|billing\/invoices)(\?(?![^#]*\bq=)[^#]*)?$/, "lookup"],
  // Wave 9: what the full forms read (never a record's own custom field values).
  [/^\/admin\/(currency\/meta|currency\/pricebooks\?status=active|billing\/gst-settings|inventory\/meta)$/, "lookup"],
  [/^\/admin\/ext\/custom-values\?module=[a-z_]+$/, "lookup"],
];
const PERSONAL = ["email", "other_emails", "phone", "pan", "billing_address", "shipping_address", "notes"];
/** What is kept of a lookup: the customer list without the people's contact details. */
function slim(path: string, data: unknown) {
  if (!/^\/admin\/customers\?/.test(path)) return data;
  const list = data as { items?: Record<string, unknown>[] };
  return { items: (list.items ?? []).map((c) => Object.fromEntries(Object.entries(c).filter(([k]) => !PERSONAL.includes(k)))) };
}
const kindOf = (path: string) => RULES.find(([re]) => re.test(path))?.[1];

function describe(kind: Kind, data: unknown): { label: string; sub: string } {
  if (kind === "customer") {
    const c = data as CustomerDetail;
    return { label: c.display_name, sub: [c.email, c.phone].filter(Boolean).join(" · ") };
  }
  if (kind === "vendor") {
    const v = data as VendorDetail;
    return { label: v.name, sub: [v.email, v.phone].filter(Boolean).join(" · ") };
  }
  if (kind === "invoice" || kind === "estimate") {
    const d = data as SalesDoc;
    return { label: d.number ?? d.label, sub: d.customer_name };
  }
  if (kind === "item") return { label: String((data as { name?: unknown }).name ?? kind), sub: "" };
  if (kind === "onboarding") {
    const o = data as { client_name?: unknown; service?: unknown };
    return { label: String(o.client_name ?? kind), sub: String(o.service ?? "") };
  }
  if (kind === "expense") return { label: String((data as { reference?: unknown; description?: unknown }).description ?? (data as { reference?: unknown }).reference ?? kind), sub: "" };
  return { label: kind, sub: "" };
}

let owner: string | null = null;
/** The offline screen's organization (after a reload with no connection); signed in, the tab's own (client.ts). */
let offlineOrg: number | null = null;
const fresh = (r: Kept) => Date.now() - r.saved_at < (r.ttl ?? TTL);
const orgNow = () => currentOrgId() ?? offlineOrg ?? 1;
const keyOf = (org: number, path: string) => `o${org}:${path}`;
const profileKey = (org: number) => `profile:o${org}`;
const mine = (r: Kept, org: number) => r.owner === owner && r.org === org;

async function save(path: string, data: unknown) {
  const kind = kindOf(path);
  if (!kind || !owner || !data || typeof data !== "object") return;
  if (kind === "invoice" && (data as SalesDoc).kind !== "invoice") return;
  if (kind === "estimate" && (data as SalesDoc).kind !== "estimate") return;
  if (kind === "onboarding" && !Array.isArray((data as { installments?: unknown }).installments)) return;
  const org = orgNow();
  await idb.put<Kept>("records", { key: keyOf(org, path), kind, owner, org, ...describe(kind, data), data: kind === "lookup" ? slim(path, data) : data, saved_at: Date.now() });
  if (kind === "meta" || kind === "lookup") return;
  const same = (await idb.all<Kept>("records")).filter((r) => r.kind === kind && r.org === org).sort((a, b) => b.saved_at - a.saved_at);
  await Promise.all(same.slice(KEEP).map((r) => idb.del("records", r.key)));
}

async function load(path: string) {
  if (!owner || !kindOf(path)) return undefined;
  const org = orgNow();
  const r = await idb.get<Kept>("records", keyOf(org, path));
  if (!r || !mine(r, org)) return undefined;
  if (!fresh(r)) {
    await idb.del("records", r.key);
    return undefined;
  }
  return r.data;
}

/**
 * Signed in: copies are kept for this person, in this organization, from now on. Anyone else's copies, and those of an
 * organization not in `member` (the organizations the person belongs to now), are deleted first; copies kept from before
 * organizations (no `org`) go too.
 */
export async function startOfflineCache(profile: OfflineProfile, member: number[] = [profile.org]) {
  owner = profile.id;
  offlineOrg = null;
  setOfflineReads({ save: (p, d) => void save(p, d).catch(() => undefined), load });
  try {
    const all = await idb.all<Kept>("records");
    await Promise.all(all.filter((r) => r.owner !== profile.id || !fresh(r) || typeof r.org !== "number" || !member.includes(r.org)).map((r) => idb.del("records", r.key)));
    await idb.put<Kept>("records", { key: profileKey(profile.org), kind: "profile", owner: profile.id, org: profile.org, label: profile.full_name, sub: profile.email, data: profile, saved_at: Date.now() });
  } catch {
    // No IndexedDB (a private window): the CRM works as before, only without offline copies.
  }
}

/** Sign-out or an expired session: every copy goes, and nothing more is kept. */
export async function clearOfflineCache() {
  owner = null;
  offlineOrg = null;
  setOfflineReads(null);
  await idb.clear("records").catch(() => undefined);
}

/**
 * A reload with no connection: who was signed in, if their copies have not expired. Turns on reading those copies, of
 * this tab's organization if it has one, else of the organization signed in to most recently on this device.
 */
export async function offlineProfile(): Promise<OfflineProfile | null> {
  try {
    const tab = currentOrgId();
    const profiles = (await idb.all<Kept<OfflineProfile>>("records")).filter((r) => r.kind === "profile" && typeof r.org === "number" && fresh(r) && (!tab || r.org === tab));
    const p = profiles.sort((a, b) => b.saved_at - a.saved_at)[0];
    if (!p) return null;
    owner = p.owner;
    offlineOrg = p.org;
    setOfflineReads({ save: () => undefined, load });
    return p.data;
  } catch {
    return null;
  }
}

/**
 * The online check (orgCheck.ts) found the organizations the person belongs to now: copies of any other organization go
 * at once, not at the next sign-in.
 */
export async function pruneOrgs(member: number[]) {
  if (!owner) return 0;
  const all = await idb.all<Kept>("records").catch(() => [] as Kept[]);
  const gone = all.filter((r) => typeof r.org !== "number" || !member.includes(r.org));
  await Promise.all(gone.map((r) => idb.del("records", r.key)));
  return gone.length;
}

/**
 * An offline edit was queued (wave 7): the kept copy shows it at once, so the record opened again offline (or edited a second
 * time) starts from what the person typed, not from before. Plain fields are copied; an invoice's lines as the form sent them.
 * The version (updated_at) is left as it was: the outbox moves it on when the first edit goes through.
 */
export async function patchKept(path: string, body: Record<string, unknown>) {
  if (!owner) return;
  const org = orgNow();
  const r = await idb.get<Kept>("records", keyOf(org, path)).catch(() => undefined);
  if (!r || !mine(r, org) || !r.data || typeof r.data !== "object") return;
  const data = { ...(r.data as Record<string, unknown>) };
  for (const [k, v] of Object.entries(body)) {
    if (k === "lines" && Array.isArray(v) && Array.isArray(data.lines)) {
      data.lines = (v as Record<string, unknown>[]).map((l, i) => ({ id: `offline_${i}`, ...l, quantity: String(l.quantity ?? ""), rate: String(l.rate ?? ""), tax_rate_in: l.tax_rate ?? null, amount: "" }));
    } else if (k in data && (v === null || typeof v !== "object")) data[k] = v;
  }
  await idb.put<Kept>("records", { ...r, data }).catch(() => undefined);
}

/**
 * A small record of this person in this organization (the reserved invoice numbers, numberBlocks.ts), cleared like the copies.
 * `ttlMs` (wave 8): how long it is kept, when not the copies' TTL_HOURS.
 */
export async function keepOwn<T>(name: string, data: T, ttlMs?: number) {
  if (!owner) return;
  const org = orgNow();
  await idb.put<Kept>("records", { key: keyOf(org, `#${name}`), kind: "block", owner, org, label: name, sub: "", data, saved_at: Date.now(), ...(ttlMs ? { ttl: ttlMs } : {}) }).catch(() => undefined);
}
export async function keptOwn<T>(name: string): Promise<T | null> {
  if (!owner) return null;
  const org = orgNow();
  const r = await idb.get<Kept<T>>("records", keyOf(org, `#${name}`)).catch(() => undefined);
  return r && mine(r, org) && fresh(r) ? r.data : null;
}

/** A kept list or setting the offline forms read (customers, items, projects…), or null. */
export async function keptLookup<T>(path: string): Promise<T | null> {
  return ((await load(path).catch(() => undefined)) as T | undefined) ?? null;
}

/** The kept customers, vendors, invoices and (wave 8) onboardings, newest first. */
export async function keptRecords(): Promise<Kept[]> {
  if (!owner) return [];
  const all = await idb.all<Kept>("records").catch(() => [] as Kept[]);
  const org = orgNow();
  return all.filter((r) => mine(r, org) && fresh(r) && (r.kind === "customer" || r.kind === "vendor" || r.kind === "invoice" || r.kind === "onboarding" || r.kind === "estimate" || r.kind === "item" || r.kind === "expense")).sort((a, b) => b.saved_at - a.saved_at);
}

/** Wave 9: what the full forms read, read once after sign-in so it is kept (a list the role may not see is skipped). */
export const FORM_LOOKUPS = [
  "/admin/customers?status=active",
  "/admin/items?status=active",
  "/admin/purchases/meta",
  "/admin/purchases/vendors",
  "/admin/sales/meta",
  "/admin/projects/meta",
  "/admin/currency/meta",
  "/admin/currency/pricebooks?status=active",
  "/admin/billing/gst-settings",
  "/admin/inventory/meta",
  ...["customers", "vendors", "items", "expenses", "invoices"].map((m) => `/admin/ext/custom-values?module=${m}`),
];
export async function keepLookups() {
  // At most once an hour per person and organization (a reload restores the session and would ask again).
  if (await keptOwn<number>("form-lookups")) return;
  for (const p of FORM_LOOKUPS) await api("GET", p).catch(() => undefined);
  await keepOwn("form-lookups", Date.now(), 3_600_000);
}

/**
 * Wave 8: the freshest kept list of this organization at `path` with any filters (`/admin/approvals` finds
 * `/admin/approvals?status=pending`), or null. The offline screen filters the rows itself.
 */
export async function keptLatest<T>(path: string): Promise<T | null> {
  if (!owner) return null;
  const org = orgNow();
  const all = await idb.all<Kept>("records").catch(() => [] as Kept[]);
  const at = keyOf(org, path);
  const r = all.filter((x) => mine(x, org) && fresh(x) && x.kind === "lookup" && (x.key === at || x.key.startsWith(`${at}?`))).sort((a, b) => b.saved_at - a.saved_at)[0];
  return r ? (r.data as T) : null;
}
